import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, Upload, Download, Trash2, MessageSquare, Send, 
  Loader2, ChevronDown, ChevronUp, File, Image, Video, X,
  FileSpreadsheet, FilePlus
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const FILE_ICONS = {
  '.pdf': FileText,
  '.doc': FileText,
  '.docx': FileText,
  '.txt': FileText,
  '.csv': FileSpreadsheet,
  '.xls': FileSpreadsheet,
  '.xlsx': FileSpreadsheet,
  '.jpg': Image,
  '.jpeg': Image,
  '.png': Image,
  '.gif': Image,
  '.mp4': Video,
  '.mov': Video,
};

export default function DocumentManager({ childId, childName, isCoachView = false }) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const fileInputRef = useRef(null);

  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  const loadDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) loadDocuments();
  }, [childId, loadDocuments]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', `Uploaded by ${user?.full_name || user?.email}`);

    try {
      await axios.post(`${API}/children/${childId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully!');
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/children/${childId}/documents/${doc.id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API}/children/${childId}/documents/${docId}`);
      toast.success('Document deleted');
      loadDocuments();
      if (selectedDoc?.id === docId) setSelectedDoc(null);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedDoc) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${API}/children/${childId}/documents/${selectedDoc.id}/comments`,
        { text: newComment }
      );
      
      // Update local state
      setDocuments(docs => docs.map(d => 
        d.id === selectedDoc.id 
          ? { ...d, comments: [...(d.comments || []), response.data.comment] }
          : d
      ));
      setSelectedDoc(prev => ({
        ...prev,
        comments: [...(prev.comments || []), response.data.comment]
      }));
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedDoc) return;

    try {
      await axios.delete(
        `${API}/children/${childId}/documents/${selectedDoc.id}/comments/${commentId}`
      );
      
      // Update local state
      setDocuments(docs => docs.map(d => 
        d.id === selectedDoc.id 
          ? { ...d, comments: (d.comments || []).filter(c => c.id !== commentId) }
          : d
      ));
      setSelectedDoc(prev => ({
        ...prev,
        comments: (prev.comments || []).filter(c => c.id !== commentId)
      }));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getFileIcon = (fileType) => {
    const Icon = FILE_ICONS[fileType] || File;
    return Icon;
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden" data-testid="document-manager">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Documents & Files</h3>
          <span className="text-sm text-zinc-500">({documents.length})</span>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="p-4 border-t border-zinc-800">
          {/* Upload Button */}
          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.mp4,.mov"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#02b5ff] hover:bg-[#0099dd] text-white"
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 mt-2">
              Supported: PDF, CSV, Word, Excel, Images, Videos (Max 50MB)
            </p>
          </div>

          {/* Document List */}
          {documents.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <FilePlus className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No documents uploaded yet</p>
              <p className="text-sm mt-1">Upload reports, test results, or other files for coach review</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Document Cards */}
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {documents.map((doc) => {
                  const FileIcon = getFileIcon(doc.file_type);
                  const hasComments = (doc.comments?.length || 0) > 0;
                  const isSelected = selectedDoc?.id === doc.id;

                  return (
                    <div
                      key={doc.id}
                      onClick={() => setSelectedDoc(doc)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-[#02b5ff]/10 border-[#02b5ff]' 
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`document-card-${doc.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded ${isSelected ? 'bg-[#02b5ff]/20' : 'bg-zinc-700'}`}>
                          <FileIcon className={`h-5 w-5 ${isSelected ? 'text-[#02b5ff]' : 'text-zinc-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{doc.original_filename}</p>
                          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                            <span>{formatFileSize(doc.file_size)}</span>
                            <span>•</span>
                            <span>{formatDate(doc.uploaded_at)}</span>
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">
                            By: {doc.uploaded_by_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {hasComments && (
                            <span className="flex items-center gap-1 text-xs text-[#02b5ff] bg-[#02b5ff]/10 px-2 py-1 rounded">
                              <MessageSquare className="h-3 w-3" />
                              {doc.comments.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Document Details & Comments Panel */}
              <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700">
                {selectedDoc ? (
                  <>
                    {/* Document Info */}
                    <div className="mb-4 pb-4 border-b border-zinc-700">
                      <h4 className="font-semibold text-white mb-2 truncate">{selectedDoc.original_filename}</h4>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(selectedDoc)}
                          className="text-xs border-zinc-600"
                          data-testid="download-doc-btn"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {isCoachOrAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(selectedDoc.id)}
                            className="text-xs border-red-600 text-red-400 hover:bg-red-600/10"
                            data-testid="delete-doc-btn"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Comments Section */}
                    <div>
                      <h5 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Coach Comments ({selectedDoc.comments?.length || 0})
                      </h5>

                      {/* Comment List */}
                      <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                        {(selectedDoc.comments || []).length === 0 ? (
                          <p className="text-sm text-zinc-500 text-center py-4">
                            No comments yet
                          </p>
                        ) : (
                          selectedDoc.comments.map((comment) => (
                            <div key={comment.id} className="bg-zinc-900/50 rounded p-3 border border-zinc-700">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-[#02b5ff]">{comment.author_name}</span>
                                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
                                      {comment.author_role}
                                    </span>
                                  </div>
                                  <p className="text-sm text-zinc-300">{comment.text}</p>
                                  <p className="text-xs text-zinc-500 mt-1">
                                    {formatDate(comment.created_at)}
                                  </p>
                                </div>
                                {(user?.id === comment.author_id || user?.role === 'admin') && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-zinc-500 hover:text-red-400 transition-colors"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add Comment (Coaches/Admins only) */}
                      {isCoachOrAdmin && (
                        <div className="flex gap-2">
                          <Textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="bg-zinc-900 border-zinc-700 text-white text-sm resize-none h-20"
                            data-testid="comment-input"
                          />
                          <Button
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || submittingComment}
                            className="bg-[#02b5ff] hover:bg-[#0099dd] self-end"
                            data-testid="submit-comment-btn"
                          >
                            {submittingComment ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>Select a document to view details</p>
                    <p className="text-sm mt-1">Coaches can add comments and feedback</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
